import React, { useCallback, useMemo, useState } from 'react';
import { gallerySites, type GallerySite } from '../data/gallery';
import { GalleryCard } from '../components/gallery/GalleryCard';
import { GalleryPreviewModal } from '../components/gallery/GalleryPreviewModal';
type Filter = {
  id: string;
  label: string;
  match: (site: GallerySite) => boolean;
};
export function Gallery() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selected, setSelected] = useState<GallerySite | null>(null);
  const close = useCallback(() => setSelected(null), []);
  const filters = useMemo<Filter[]>(() => {
    const industries = Array.from(new Set(gallerySites.map(s => s.industry)));
    return [{
      id: 'all',
      label: 'All',
      match: () => true
    }, {
      id: 'websites',
      label: 'Websites',
      match: s => s.type === 'website'
    }, {
      id: 'landing',
      label: 'Landing Pages',
      match: s => s.type === 'landing page'
    }, ...industries.map(industry => ({
      id: `industry:${industry}`,
      label: industry,
      match: (s: GallerySite) => s.industry === industry
    }))];
  }, []);
  const current = filters.find(f => f.id === activeFilter) ?? filters[0];
  const visible = gallerySites.filter(current.match);
  return <>
      <section className="bg-brand-black px-[clamp(1rem,4vw,6rem)] pb-16 pt-14 lg:pb-20 lg:pt-20" data-id="element-1195" data-mp="2baczvuj9dc7u">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-brand-red" data-id="element-1196" data-mp="2fadb76b297ao">Our work</p>
        <h1 className="mt-4 max-w-[20ch] text-[clamp(2.5rem,3.6vw,4.25rem)] font-extrabold leading-[1.05] tracking-tight text-white" data-id="element-1197" data-mp="330hflfw2pb2q">
          Real businesses. Real websites. <span className="text-brand-red" data-id="element-1198" data-mp="2tzt3wru5jmt0">Built fast.</span>
        </h1>
        <p className="mt-6 max-w-[60ch] text-[clamp(1.0625rem,1.2vw,1.375rem)] leading-relaxed text-gray-300" data-id="element-1199" data-mp="2yxuj31ogj1z7">
          Every one of these started as a 3-day build and keeps growing one change at a time.
        </p>
      </section>

      <section aria-label="Website gallery" className="bg-white px-[clamp(1rem,4vw,6rem)] py-12 lg:py-16" data-id="element-1200" data-mp="1njz1zndzsj5c">
        <div role="group" aria-label="Filter gallery" className="flex flex-wrap gap-2" data-id="element-1201" data-mp="3p6saxojmoomq">
          {filters.map(filter => {
          const isActive = filter.id === current.id;
          return <button key={filter.id} type="button" aria-pressed={isActive} onClick={() => setActiveFilter(filter.id)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 ${isActive ? 'bg-brand-red text-white' : 'bg-gray-100 text-brand-black hover:bg-brand-light'}`} data-id="element-1202" data-mp="3fgqvfl5ccejf">
                {filter.label}
              </button>;
        })}
        </div>

        <p className="mt-6 text-sm text-brand-gray" aria-live="polite" data-id="element-1203" data-mp="qqr2t9alsy1b">
          Showing {visible.length} {visible.length === 1 ? 'site' : 'sites'}
        </p>

        <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[2560px]:grid-cols-5" data-id="element-1204" data-mp="28nugo52v3bpu">
          {visible.map(site => <li key={site.id} data-id="element-1205" data-mp="2ide13f683dmt">
              <GalleryCard site={site} onOpen={setSelected} data-id="element-1206" data-mp="2iefmodkv7iis" />
            </li>)}
        </ul>
      </section>

      <section className="bg-brand-red px-[clamp(1rem,4vw,6rem)] py-20 text-center lg:py-24" data-id="element-1207" data-mp="2mepuzww4ql0t">
        <h2 className="text-[clamp(2.25rem,3vw,3.5rem)] font-extrabold tracking-tight text-white" data-id="element-1208" data-mp="1pr2xdvnbkwls">Want yours next?</h2>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row" data-id="element-1209" data-mp="1uq64rmqrvewx">
          <a href="#/contact" className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-brand-black px-8 py-4 text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-[#1f1f1f] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-red" data-id="element-1210" data-mp="1ys12yidepc6h">
            Talk to us
          </a>
          <a href="#/design/websites" className="inline-flex items-center justify-center whitespace-nowrap rounded-md border-2 border-white/60 px-8 py-4 text-lg font-bold text-white transition-colors duration-150 ease-out hover:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-red" data-id="element-1211" data-mp="38o6ar7hdcca7">
            See Living Websites
          </a>
        </div>
      </section>

      <GalleryPreviewModal site={selected} onClose={close} data-id="element-1212" data-mp="3rkxybt3y1dk2" />
    </>;
}